<i18n>
{
  "fr": {
    "zoneInputText": {
      "label": "Ajouter un texte",
      "placeholder": "@:zoneInputText.label"
    }
  }
}
</i18n>

<template>
  <BaseInputContent
    :class="[BLOCK_SELECTOR, { [`${BLOCK_SELECTOR}--is-empty`]: !hasContent }]"
    v-bind="{
      allowEdit: customProduct && hasContent,
      allowDelete: customProduct && hasContent,
    }"
    @edit="onEdit"
    @delete="onDelete"
  >
    <template #inputPrepend>
      <span class="zone-input-text__icon"></span>
    </template>

    <template #main>
      <label
        :for="name"
        class="zone-input-text__label"
        v-text="$t('zoneInputText.placeholder')"
      ></label>
      <input
        :id="name"
        type="text"
        :value="localValue"
        class="zone-input-text__input"
        :placeholder="$t('zoneInputText.placeholder')"
        @input="onInput"
        @focus="onFocus"
        @blur="$emit('blur')"
      />

      <ConfiguratorLayerEditorModal
        v-if="customization"
        v-bind="{
          group,
          color:
            (isActiveScreenPrintingColor &&
              activeScreenPrintingColor &&
              activeScreenPrintingColor.value) ||
            undefined,
        }"
        :active.sync="isModalActive"
        :customization.sync="computedCustomization"
      />
    </template>
  </BaseInputContent>
</template>

<script>
import BaseInputContent from './BaseInputContent';
import ConfiguratorLayerEditorModal from '../Modals/ConfiguratorLayerEditorModal';
import {
  ConfiguratorGetters,
  ConfiguratorMutation,
  mapGetters as mapConfiguratorGetters,
  prefixType as prefixConfiguratorType,
} from '../../../store/modules/configurator';
import ZoneInputEditorModal from '../../../mixins/ZoneInputEditorModal';

export default {
  name: 'ZoneInputText',
  components: { ConfiguratorLayerEditorModal, BaseInputContent },
  mixins: [ZoneInputEditorModal],
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: undefined,
    },
    customProduct: Boolean,
  },
  data() {
    return {
      BLOCK_SELECTOR: 'zone-input-text',
      localValue: undefined,
      isModalActive: false,
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      isActiveScreenPrintingColor:
        ConfiguratorGetters.IS_ACTIVE_PRODUCT_VARIANT_SCREEN_PRINTING,
      activeScreenPrintingColor:
        ConfiguratorGetters.GET_ACTIVE_SCREEN_PRINTING_COLOR,
    }),
    computedCustomization: {
      get() {
        return this.customization || undefined;
      },
      set(v) {
        const zone = this.group.zoneMap[this.customization.templateZoneId];
        const customization = {
          ...v,
          // Ensure id, type and templateZoneId have not been updated
          id: this.customization.id,
          type: this.customization.type,
          templateZoneId: zone.id,
        };

        this.$store.commit(
          prefixConfiguratorType(ConfiguratorMutation.SET_ZONE_CUSTOMIZATION),
          {
            groupId: zone.groupId === null ? undefined : zone.groupId,
            customization,
          }
        );
      },
    },
    hasContent: {
      get() {
        return this.value && this.value.trim() !== '';
      },
    },
  },
  watch: {
    value: {
      handler(value) {
        this.localValue = value || undefined;
      },
      immediate: true,
    },
    computedCustomization(customization) {
      if (customization.text) {
        this.localValue = customization.text;
      }
    },
    isModalActive(isModalACtive) {
      this.$emit(isModalACtive ? 'focus' : 'blur');
    },
  },
  methods: {
    onInput(e) {
      this.localValue = e.target.value;

      this.$emit('input', this.localValue);
    },
    onFocus(e) {
      if (this.customProduct) {
        e.target.blur();

        this.isModalActive = true;

        return;
      }

      this.$emit('focus');
    },
    onEdit() {
      this.isModalActive = true;
    },
    onDelete() {
      this.$store.commit(
        prefixConfiguratorType(ConfiguratorMutation.DELETE_ZONE_CUSTOMIZATION),
        this.customization.id
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.zone-input-text {
  $block-selector: &;

  border-radius: 0.75rem;
  border: 1px solid $color-arapawa;
  padding-left: 0.5rem;

  &--is-empty {
    padding-left: 0;
    border: 1px dashed $color-arapawa;

    &::v-deep .base-input-content__input-wrapper {
      flex: unset;
    }

    &::v-deep .base-input-content__input-group {
      justify-content: center;
    }
  }

  &__icon {
    &::before {
      @extend %icon;
      @extend %icon--text;

      color: $color-arapawa;
    }

    #{$block-selector}--is-empty & {
      &::before {
        @extend %icon--plus;
      }
    }
  }

  &__label {
    @include sr-only();
  }

  &__input {
    width: 100%;
    border: none;
    padding: 1rem 0.3125rem;
    color: $color-arapawa;
    background: none;

    &::placeholder {
      color: inherit;
      font: inherit;
    }

    #{$block-selector}--is-empty & {
      text-align: center;
      text-transform: uppercase;
      font-weight: 600;
      width: fit-content;
      padding: 1.125rem 0 0.875rem;
    }
  }
}
</style>
